<template>
  <div class="explore-page">
    <main class="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- 页面标题 -->
      <div class="text-center mb-12">
        <h1 class="text-3xl md:text-4xl font-bold mb-4">探索AI工具</h1>
        <p class="text-neutral-500 max-w-2xl mx-auto">
          浏览我们丰富的AI工具分类，从教学应用到办公效率，总有一款适合您的需求
        </p>
      </div>

      <!-- 工具搜索 -->
      <div class="mb-8">
        <div class="flex flex-col md:flex-row gap-4 mb-4">
          <!-- 分类筛选 -->
          <div class="relative min-w-[150px]">
            <select
              v-model="selectedCategory"
              @change="handleSearch"
              class="w-full px-4 py-3 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary appearance-none bg-white"
            >
              <option value="all">所有分类</option>
              <option
                v-for="category in categoryList"
                :key="category.id"
                :value="category.id"
              >
                {{ category.title }}
              </option>
            </select>
            <i
              class="fa fa-chevron-down absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 pointer-events-none"
            ></i>
          </div>

          <!-- 科目筛选 -->
          <div class="relative min-w-[150px]">
            <select
              v-model="selectedSub"
              @change="handleSearch"
              class="w-full px-4 py-3 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary appearance-none bg-white"
            >
              <option value="all">所有学科</option>
              <option v-for="sub in subList" :key="sub.id" :value="sub.id">
                {{ sub.title }}
              </option>
            </select>
            <i
              class="fa fa-chevron-down absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 pointer-events-none"
            ></i>
          </div>

          <div class="relative flex-1">
            <input
              type="text"
              v-model="searchQuery"
              class="w-full px-4 py-3 pl-10 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              placeholder="搜索工具..."
              @keyup.enter="handleSearch"
            />
            <i
              class="fa fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400"
            ></i>
          </div>
        </div>
      </div>

      <!-- 分类展示区域 (无搜索时显示) -->
      <section v-if="!isSearch" class="mb-16">
        <h2 class="text-2xl font-bold mb-6">工具分类</h2>

        <div
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          id="categories-container"
        >
          <div
            v-for="category in categoryList"
            :key="category.id"
            class="category-card bg-white rounded-xl shadow-sm border border-neutral-200 p-6 category-card-hover cursor-pointer"
            @click="navigateToCategory(category)"
          >
            <div
              class="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
              :style="{
                backgroundColor: category.color + '20',
                color: category.color,
              }"
            >
              <i :class="['fa', category.icon, 'text-xl']"></i>
            </div>
            <h3 class="text-lg font-bold mb-2">{{ category.title }}</h3>
            <p class="text-neutral-500 text-sm mb-3">
              {{ category.description }}
            </p>
            <div class="flex justify-between items-center">
              <span class="text-sm text-neutral-400"
                >{{ category.toolCount }} 个工具</span
              >
              <i class="fa fa-arrow-right text-primary"></i>
            </div>
          </div>
        </div>
      </section>

      <section v-if="!isSearch" class="mb-16">
        <h2 class="text-2xl font-bold mb-6">学科分类</h2>

        <div
          class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-16"
        >
          <div
            v-for="subject in subList"
            :key="subject.id"
             class="category-card bg-white rounded-xl shadow-sm border border-neutral-200 p-6 category-card-hover cursor-pointer"
            @click="navigateToSub(subject)"
          >
            <h3 class="text-lg font-bold mb-2">{{ subject.title }}</h3>
            <div class="flex justify-between items-center">
              <span class="text-sm text-neutral-400"
                >{{ subject.toolCount }} 个工具</span
              >
              <i class="fa fa-arrow-right text-primary"></i>
            </div>
          </div>
        </div>
      </section>

      <!-- 搜索结果 (有搜索时显示) -->
      <section v-else class="mb-16">
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-2xl font-bold">搜索结果</h2>
          <button
            @click="(searchQuery = ''), (isSearch = false)"
            class="text-neutral-500 hover:text-primary text-sm"
          >
            清除搜索
          </button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="tool in tools"
            :key="tool.id"
            class="bg-white rounded-xl border border-neutral-200 overflow-hidden hover-lift transition-all duration-300 cursor-pointer"
            @click="openToolDetail(tool)"
          >
            <div class="relative h-48 overflow-hidden group">
              <img
                :src="tool.cover"
                :alt="tool.title"
                class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <!-- <div
                class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center space-x-3"
              >
                <button
                  class="p-2 bg-white rounded-full text-neutral-800 hover:text-primary transition-colors"
                  title="收藏"
                  @click.stop
                >
                  <i class="fa fa-heart-o"></i>
                </button>
                <button
                  class="p-2 bg-white rounded-full text-neutral-800 hover:text-primary transition-colors"
                  title="下载"
                  @click.stop
                >
                  <i class="fa fa-download"></i>
                </button>
              </div> -->
              <div class="absolute top-3 right-3 flex">
                <div
                  class="bg-white/90 backdrop-blur-sm px-2 py-1 rounded text-xs font-medium text-neutral-600"
                >
                  {{ getCategoryName(tool.categoryid) }}
                </div>
                <div
                  class="ml-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded text-xs font-medium text-neutral-600"
                  v-if="getSubName(tool.subid).length > 0"
                >
                  {{ getSubName(tool.subid) }}
                </div>
              </div>
            </div>

            <div class="p-5">
              <div class="flex justify-between items-start mb-2">
                <h3 class="font-bold text-lg line-clamp-1">{{ tool.title }}</h3>
                <div class="flex items-center text-yellow-400 text-sm">
                  <i class="fa fa-star mr-1"></i>
                  <span>{{ tool.rating }}</span>
                </div>
              </div>
              <p class="text-neutral-500 text-sm mb-4 line-clamp-2 h-10">
                {{ tool.description }}
              </p>

              <div
                class="flex justify-between items-center pt-4 border-t border-neutral-100"
              >
                <div class="flex items-center space-x-3">
                  <div class="flex items-center text-xs text-neutral-400">
                    <i class="fa fa-download mr-1"></i>
                    {{ formatNumber(tool.downloadCount) }}
                  </div>
                  <div class="flex items-center text-xs text-neutral-400">
                    <i class="fa fa-clock-o mr-1"></i>
                    {{ formatDate(tool.createTime) }}
                  </div>
                </div>
                <button
                  class="text-primary hover:text-primary-dark text-sm font-medium"
                >
                  查看详情
                </button>
              </div>
            </div>
          </div>

          <div class="flex justify-end w-full col-span-1 md:col-span-3">
            <el-pagination
              background
              layout="prev, pager, next"
              :total="total"
              @current-change="pageChange"
            />
          </div>
        </div>

        <div v-if="tools.length === 0" class="text-center py-12">
          <p class="text-neutral-500">未找到相关工具</p>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { globalState } from "@/state";

export default {
  name: "ExplorePage",
  data() {
    return {
      categoryList: [],
      isSearch: false,
      tools: [],
      searchQuery: "",
      page: 1,
      pageSize: 20,
      total: 0,
      subList: [],
      selectedCategory: "all",
      selectedSub: "all",
    };
  },
  computed: {
    // filteredTools() {
    //   if (!this.searchQuery) return [];
    //   const query = this.searchQuery.toLowerCase();
    //   return this.tools.filter(
    //     (tool) =>
    //       tool.name.toLowerCase().includes(query) ||
    //       tool.description.toLowerCase().includes(query)
    //   );
    // },
  },
  created() {
    this.getCategory();
    if (this.$route.query.category) {
      this.selectedCategory = this.$route.query.category;
      this.handleSearch();
    }
  },
  methods: {
    getCategory() {
      this.$axios({
        url: "subinfo/list/count",
      }).then((res) => {
        this.subList = res.data;
      });
      this.$axios({
        url: "category/list/count",
      }).then((res) => {
        this.categoryList = res.data;
      });
    },

    async pageChange(page) {
      this.page = page;
      await this.handleSearch();
    },
    async handleSearch() {
      this.isSearch = true;
      var data = await this.$aesEncrypt({
        searchKey: this.searchQuery,
        categoryId: this.selectedCategory,
        subId: this.selectedSub,
        page: this.page,
        pageSize: this.pageSize,
      });
      this.$axios({
        url: "aitool/search",
        method: "POST",
        data: {
          data: data,
        },
      }).then((res) => {
        this.tools = res.data.list;
      });
    },
    openToolDetail(tool) {
      globalState.openModal(tool.id, this.categoryList);
    },
    getSubName(id) {
      var sub = this.subList.find((c) => c.id === id);
      return sub ? sub.title : "";
    },
    navigateToCategory(category) {
      this.$router.push({
        path: "/categories",
        query: { category: category.id },
      });
    },
     navigateToSub(subject) {
      this.$router.push({
        path: "/subjects",
        query: { subject: subject.id },
      });
    },
    getCategoryName(id) {
      const category = this.categoryList.find((c) => c.id === id);
      return category ? category.title : "未知";
    },

    formatNumber(num) {
      if (num >= 1000) {
        return (num / 1000).toFixed(1) + "k";
      }
      return num;
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString("zh-CN", {
        month: "numeric",
        day: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.category-card-hover {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.category-card-hover:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1),
    0 8px 10px -6px rgba(0, 0, 0, 0.1);
}
</style>
